import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";

export type RefundSource = "deallocation" | "return" | "damage" | "manual_cn";

export type RefundDestination = "wallet" | "bank";

export type RefundStage =
  | "processing"
  | "in_wallet"
  | "awaiting_payout"
  | "partially_paid"
  | "paid"
  | "payout_failed"
  | "cancelled"
  | "failed";

export interface RefundTimelineEvent {
  label: string;
  at: string | null;
}

export interface RefundPayout {
  utr: string | null;
  amount_rupees: string;
  paid_on: string | null;
  paid_from: string | null;
  refund_mode: string | null;
  zoho_refund_id: string | null;
}

export interface RefundTrackerRow {
  key: string;
  escalation_id: number | null;
  wallet_transaction_id: number | null;
  return_escalation_id: number | null;
  source: RefundSource;
  source_label: string;
  destination: RefundDestination;
  moved_from_wallet: boolean;
  stage: RefundStage;
  stage_label: string;
  amount_paise: string;
  amount_rupees: string;
  outstanding_paise: string;
  outstanding_rupees: string;
  paid_rupees: string;
  created_at: string;
  sales_order_id: number | null;
  sales_order_number: string | number | null;
  lead_id: string | null;
  customer_name: string | null;
  customer_contact: string | null;
  client_id: string | null;
  requested_by: string | null;
  requested_by_id: string | null;
  reason_code: string | null;
  notes: string;
  items: { name: string | null; qty: string | null }[];
  charges: string[];
  cn_number: string | null;
  zoho_creditnote_id: string | null;
  payouts?: RefundPayout[];
  timeline: RefundTimelineEvent[];
}

export interface RefundTrackerSummary {
  total: number;
  total_rupees: string;
  pending: number;
  pending_rupees: string;
  stages: Record<RefundStage, number>;
  stage_rupees: Record<RefundStage, string>;
  sources: Record<RefundSource, number>;
}

export interface RaisedByOption {
  id: string;
  name: string;
  count: number;
}

export interface RefundTrackerResponse {
  count: number;
  page: number;
  page_size: number;
  summary: RefundTrackerSummary;
  raised_by_options: RaisedByOption[];
  results: RefundTrackerRow[];
}

export interface RefundTrackerQuery {
  search?: string;
  source?: RefundSource[];
  destination?: RefundDestination[];
  stage?: RefundStage[];
  raised_by?: string[];
  pending?: boolean;
  date_from?: string;
  date_to?: string;
  page: number;
  page_size: number;
}

export async function fetchRefundTracker(
  query: RefundTrackerQuery
): Promise<RefundTrackerResponse | undefined> {
  const params: Record<string, string | number> = {
    page: query.page,
    page_size: query.page_size,
  };
  if (query.search) params.search = query.search;
  if (query.source?.length) params.source = query.source.join(",");
  if (query.destination?.length) params.destination = query.destination.join(",");
  if (query.raised_by?.length) params.raised_by = query.raised_by.join(",");
  if (query.stage?.length) params.stage = query.stage.join(",");
  if (query.pending) params.pending = "true";
  if (query.date_from) params.date_from = query.date_from;
  if (query.date_to) params.date_to = query.date_to;
  return axios
    .get("/oms/refunds/tracker/", { params })
    .then((res) => res?.data)
    .catch((error) => {
      errorActions(error, "Error loading refunds");
      return undefined;
    });
}
